import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import type { SafetyProfilePayload } from "@miter/types/contracts";
import { Button, Loader, TooltipProvider } from "ui2";

import { AboutSection } from "safety/components/profile/AboutSection";
import { ContactSection } from "safety/components/profile/ContactSection";
import { DocumentsSection } from "safety/components/profile/DocumentsSection";
import { FaqSection } from "safety/components/profile/FaqSection";
import { formatDate, FULL_DATE } from "safety/components/profile/format";
import { HeadlineMetrics } from "safety/components/profile/HeadlineMetrics";
import { PhotoHeader } from "safety/components/profile/PhotoHeader";
import { SafetyProfileHeader } from "safety/components/profile/SafetyProfileHeader";
import { SafetyProfileIdentity } from "safety/components/profile/SafetyProfileIdentity";
import { StatSection } from "safety/components/profile/StatSection";
import { PublicSafetyPageContainer, PublicSafetyStatusMessage } from "safety/components/PublicSafetyPage";
import { MARKETING_URL } from "safety/constants";
import { MiterAPI, SafetyProfileNotFoundError } from "safety/miter";

const buildPublishedWithMiterUrl = (slug: string): string => {
  const url = new URL("/field-operations/safety/", MARKETING_URL);
  url.search = new URLSearchParams({
    utm_source: "safety_profile",
    utm_medium: "referral",
    utm_campaign: "powered_by",
    utm_content: slug,
  }).toString();
  return url.toString();
};

const SafetyProfile = ({
  profile,
  slug,
}: {
  profile: SafetyProfilePayload;
  slug: string;
}): React.JSX.Element => (
  <>
    <title>{`${profile.company.name} safety profile`}</title>
    <div data-theme="new" className="tw:contents">
      <PublicSafetyPageContainer scroll="page">
        <SafetyProfileHeader />
        <div className="tw:flex-1 tw:bg-off-white">
          {profile.header_photos && <PhotoHeader photos={profile.header_photos} />}
          <SafetyProfileIdentity company={profile.company} />
          {profile.rate_band && <HeadlineMetrics metrics={profile.rate_band.metrics} />}
          <div className="tw:flex tw:flex-col tw:gap-6 tw:px-4 tw:py-6">
            {profile.about && <AboutSection about={profile.about} />}
            {!!profile.incident_stats?.length && (
              <StatSection title="Incidents and filings" stats={profile.incident_stats} />
            )}
            {!!profile.program_stats?.length && (
              <StatSection title="Program evidence" stats={profile.program_stats} />
            )}
            {!!profile.documents?.length && <DocumentsSection documents={profile.documents} />}
            {!!profile.faqs?.length && <FaqSection faqs={profile.faqs} />}
            {!!profile.contacts?.length && <ContactSection contacts={profile.contacts} />}
          </div>
        </div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-stroke tw:px-4 tw:py-3 tw:text-sm tw:text-midnight/50">
          <span>Current as of {formatDate(profile.as_of, FULL_DATE)}</span>
          <a
            href={buildPublishedWithMiterUrl(slug)}
            target="_blank"
            rel="noopener"
            className="tw:text-inherit tw:underline-offset-2 tw:hover:text-midnight tw:hover:underline"
          >
            Published with Miter
          </a>
        </div>
      </PublicSafetyPageContainer>
    </div>
  </>
);

export const SafetyProfilePage = (): React.JSX.Element => {
  const { slug } = useParams<{ slug: string }>();

  const {
    data: profile,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["safety-profile", slug],
    queryFn: () => MiterAPI.safety.profiles.retrieve(slug!),
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) {
    return (
      <PublicSafetyPageContainer>
        <Loader className="tw:flex-1" size="lg" />
      </PublicSafetyPageContainer>
    );
  }

  if (!slug || error instanceof SafetyProfileNotFoundError) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Safety profile not found"
          description="This link is invalid or the contractor has taken their safety profile down. Double-check the link, or ask them to share it again."
        />
      </PublicSafetyPageContainer>
    );
  }

  if (!profile) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Unable to load this safety profile"
          description="Something went wrong while loading this page. Check your connection and try again."
          action={
            <Button variant="outline" onClick={() => void refetch()} disabled={isRefetching}>
              Try again
            </Button>
          }
        />
      </PublicSafetyPageContainer>
    );
  }

  return (
    <TooltipProvider>
      <SafetyProfile profile={profile} slug={slug} />
    </TooltipProvider>
  );
};
